/**
 * Рендерер конверта ответа: один компонент на каждый блок контракта.
 *
 * Зачем он существует отдельно от экранов. Ответ шлюза — не текст и не
 * «виджет», а СПЕЦИФИКАЦИЯ из типизированных блоков, и рисовать её обязан один
 * код на весь продукт. Иначе карман района, карточка контура и станция ответов
 * нарисовали бы одну и ту же развилку «сделано / не сделано» тремя разными
 * способами, и человек читал бы три разных ответа на один вопрос.
 *
 * Правила, которые держит именно этот модуль:
 *
 * * **ни одной собственной строки текста о данных.** Всё, что говорит о
 *   величинах, приходит фразой с сервера (`Phrase.text`). Здесь есть только
 *   подписи интерфейса — «как это собрано», «показать остальные», — то есть
 *   слова о САМОМ ЭКРАНЕ, а не о корпусе;
 * * **канонические примитивы, а не свои.** Две оси — `DoneSplit`, крупные
 *   числа — `MetricCard`, раскрытие — `Disclosure`, отметка класса —
 *   `EvidenceBadge`, пустое — `DataStatePanel`. Блок, нарисованный мимо них,
 *   завтра разойдётся с экраном, который рисует то же самое;
 * * **бюджет плотности соблюдается и внутри блока.** Сервер перенёс лишние
 *   БЛОКИ в раскрытие; список внутри блока и строки таблицы режет клиент — их
 *   сервер делить не вправе;
 * * **отсутствие названо.** `missing` печатается как «нет данных», пустая
 *   таблица — фразой сервера, неполное покрытие — подвалом ограничений;
 * * **переход ведёт на уровень.** Действие рисуется ссылкой только на
 *   известный клиенту адрес; остальное называется словами и никуда не ведёт;
 * * **участие модели названо на каждом ответе И на каждом отказе.** Конверт
 *   несёт `model` в обоих видах, и «языковая модель не использовалась» —
 *   утверждение, ради которого шлюз и заведён: молча его подразумевать значит
 *   оставить человека гадать. Плашка, стоящая только на ответах, выборочна, а
 *   значит ненадёжна: отказ модели и отказ правила реестра — разные события с
 *   разной ценой ошибки;
 * * **основание показывается вместе с трассой, и трасса не выдаётся за
 *   чужую.** Конверт `answer` списка проверок не несёт и нести не будет;
 *   «как это собрано» пересчитывает правила КЛИЕНТОМ по тому же конверту и
 *   говорит это вслух. Расхождение здесь означает не спорные данные, а то,
 *   что ответ и его проверка разошлись по дороге.
 *
 * Три исхода конверта различаются формой, а не оттенком: `answer` —
 * спецификация, `abstention` — отказ словами сервера, `rejected` — поломка
 * реализации, о которой человеку говорят честно и без внутреннего объяснения
 * (оно принадлежит журналу).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  AiAbstention,
  AiAnswerOk,
  AiBlock,
  AiEnvelope,
  AiModelUse,
  AiPhrase,
  AiRejected,
  AiResolvedAnswer,
  AiValueRef,
} from "../../api/client";
import type { RoleKind } from "../../auth/context";
import { Bars } from "../../components/Bars";
import { DetailDrawer } from "../../components/DetailDrawer";
import { Disclosure } from "../../components/Disclosure";
import { DoneSplit } from "../../components/Progress";
import { Timeline } from "../../components/Timeline";
import {
  VirtualTable,
  tableStyles,
  type Column,
} from "../../components/VirtualTable";
import {
  Badge,
  Button,
  Card,
  DataStatePanel,
  EvidenceBadge,
  InlineAlert,
  MetricCard,
} from "../../components/ui";
import { DENSITY, type LevelId } from "../../lib/levels";
import { ROUTES } from "../../lib/routes";
import {
  knownRoute,
  numberOf,
  phraseText,
  showById,
  showValue,
  splitByBudget,
} from "./answerModel";
import { AnswerContourPreview, selectionContours } from "./AnswerMap";
import {
  traceDrift,
  traceRecounted,
  validatorTrace,
  type TraceRow,
} from "./validatorTrace";
import styles from "./ai.module.css";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { tr } from "../../i18n/runtime";
import { srv } from "../../i18n/serverText";
import { levelWord } from "./levelWords";

const m = defineMessages({
  phraseMissing: {
    id: "answer.spec.phraseMissing",
    defaultMessage: "текст фразы не собран сервером",
    description: "Пометка на месте фразы ответа ИИ, которую сервер не смог собрать (ошибка сборки); коротко, строчными",
  },
  deadEndHint: {
    id: "answer.spec.deadEnd.hint",
    defaultMessage: "адрес ответа: {to}",
    description: "Подсказка у перехода без экрана в ответе ИИ; {to} — служебный адрес перехода",
  },
  deadEnd: {
    id: "answer.spec.deadEnd.text",
    defaultMessage: "{label} — экрана для этого перехода в этой версии нет",
    description: "Текст вместо ссылки в ответе ИИ, когда экрана для перехода нет; {label} — название перехода",
  },
  demoTitle: {
    id: "answer.spec.demo.title",
    defaultMessage: "Показаны демонстрационные данные",
    description: "Заголовок плашки в ответе ИИ: в счёт включены демонстрационные данные",
  },
  restNumbers: {
    id: "answer.spec.kpi.rest",
    defaultMessage: "Остальные числа",
    description: "Заголовок раскрытия с остальными числами в ответе ИИ",
  },
  moreSummary: {
    id: "answer.spec.more.summary",
    defaultMessage: "ещё {count}",
    description: "Подпись-сводка раскрытия «Остальные …» в ответе ИИ; {count} — сколько скрыто; коротко",
  },
  moreAction: {
    id: "answer.spec.more.action",
    defaultMessage: "показать ещё {count}",
    description: "Кнопка раскрытия скрытых пунктов в ответе ИИ; {count} — сколько скрыто; коротко",
  },
  gapTitle: {
    id: "answer.spec.gap.title",
    defaultMessage: "Чего в ответе нет",
    description: "Заголовок блока пробелов в ответе ИИ: каких данных не хватает",
  },
  gapMore: {
    id: "answer.spec.gap.more",
    defaultMessage: "Остальные пробелы",
    description: "Заголовок раскрытия с остальными пробелами данных в ответе ИИ",
  },
  chainTitle: {
    id: "answer.spec.chain.title",
    defaultMessage: "Как получено",
    description: "Заголовок карточки с цепочкой происхождения числа в ответе ИИ",
  },
  chainSources: {
    id: "answer.spec.chain.sources",
    defaultMessage: "источников: {count}",
    description: "Подпись шага цепочки происхождения в ответе ИИ; {count} — число источников; строчными",
  },
  draftBadge: {
    id: "answer.spec.action.draft",
    defaultMessage: "черновик",
    description: "Бейдж у действия в ответе ИИ: действие готовит черновик; коротко",
  },
  draftHint: {
    id: "answer.spec.action.draftHint",
    defaultMessage: "Действие готовит черновик, а не выполняет запись",
    description: "Подсказка у бейджа «черновик» в ответе ИИ",
  },
  open: {
    id: "answer.spec.link.open",
    defaultMessage: "открыть",
    description: "Ссылка у отклонения в ответе ИИ: перейти к подробностям; строчными, коротко",
  },
  deviationsTitle: {
    id: "answer.spec.deviations.title",
    defaultMessage: "Что отклонилось",
    description: "Заголовок карточки с отклонениями от плана в ответе ИИ",
  },
  deviationsMore: {
    id: "answer.spec.deviations.more",
    defaultMessage: "Остальные отклонения",
    description: "Заголовок раскрытия с остальными отклонениями в ответе ИИ",
  },
  causesTitle: {
    id: "answer.spec.causes.title",
    defaultMessage: "Почему так",
    description: "Заголовок карточки с причинами в ответе ИИ",
  },
  observedHint: {
    id: "answer.spec.causes.observedHint",
    defaultMessage: "Связь видна в записях, на которые ссылается основание",
    description: "Подсказка у бейджа «наблюдаемо» у причины в ответе ИИ",
  },
  hypothesisHint: {
    id: "answer.spec.causes.hypothesisHint",
    defaultMessage: "Гипотеза: связь не доказана данными среза",
    description: "Подсказка у бейджа «гипотеза» у причины в ответе ИИ (срез — снимок данных на дату)",
  },
  observed: {
    id: "answer.spec.causes.observed",
    defaultMessage: "наблюдаемо",
    description: "Бейдж у причины в ответе ИИ: связь видна в данных; коротко",
  },
  hypothesis: {
    id: "answer.spec.causes.hypothesis",
    defaultMessage: "гипотеза",
    description: "Бейдж у причины в ответе ИИ: связь предположена, не доказана; коротко",
  },
  basis: {
    id: "answer.spec.causes.basis",
    defaultMessage: "основание: {method}",
    description: "Подпись под причиной в ответе ИИ; {method} — как получен вывод (текст сервера)",
  },
  basisSources: {
    id: "answer.spec.causes.basisSources",
    defaultMessage: "основание: {method} · записей: {count}",
    description: "Подпись под причиной в ответе ИИ; {method} — как получен вывод, {count} — число записей-источников",
  },
  noBasis: {
    id: "answer.spec.causes.noBasis",
    defaultMessage: "основание не названо",
    description: "Пометка у причины в ответе ИИ, у которой нет основания",
  },
  nounField: {
    id: "answer.spec.split.noun",
    defaultMessage: "{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}",
    description: "Единица счёта под полосой «отчитались» в ответе ИИ: слово после числа («без отчёта: 5 работ на полях»), без самого числа",
  },
  splitDrift: {
    id: "answer.spec.split.drift",
    defaultMessage: "Сервер называет «без отчёта» иначе, чем остаток от счёта: {value}",
    description: "Предупреждение в ответе ИИ: число «без отчёта» от сервера расходится с остатком; {value} — число сервера",
  },
  splitNoValues: {
    id: "answer.spec.split.noValues",
    defaultMessage: "Счёт объявлен, но величин в ответе нет",
    description: "Пояснение в ответе ИИ: ответ обещал счёт, но чисел не прислал",
  },
  tableEmpty: {
    id: "answer.spec.table.empty",
    defaultMessage: "Строк нет",
    description: "Заголовок пустой таблицы в ответе ИИ",
  },
  tableEmptyNoReason: {
    id: "answer.spec.table.emptyNoReason",
    defaultMessage: "причина пустоты не названа",
    description: "Пометка в пустой таблице ответа ИИ: сервер не назвал причину",
  },
  tableGrain: {
    id: "answer.spec.table.grain",
    defaultMessage: "Одна строка — {grain}",
    description: "Подпись над таблицей в ответе ИИ; {grain} — что такое одна строка (район, хозяйство, контур…)",
  },
  tableGrainDenominator: {
    id: "answer.spec.table.grainDenominator",
    defaultMessage: "Одна строка — {grain} · знаменатель: {denominator}",
    description: "Подпись над таблицей в ответе ИИ; {grain} — что такое одна строка, {denominator} — в чём считается доля (текст сервера)",
  },
  tableMore: {
    id: "answer.spec.table.more",
    defaultMessage: "Остальные строки",
    description: "Заголовок раскрытия с остальными строками таблицы в ответе ИИ",
  },
  tableCaption: {
    id: "answer.spec.table.caption",
    defaultMessage: "Остальные строки ответа: {count}",
    description: "Подпись (для чтения с экрана) таблицы с остальными строками в ответе ИИ; {count} — число строк",
  },
  tableMeta: {
    id: "answer.spec.table.meta",
    defaultMessage: "Строк в ответе: {total} · на виду {preview} · здесь остальные {hidden}",
    description: "Строка-сводка над таблицей остальных строк в ответе ИИ; все аргументы — числа",
  },
  mapTitle: {
    id: "answer.spec.map.title",
    defaultMessage: "Где это на земле",
    description: "Заголовок карточки карты в ответе ИИ",
  },
  mapLayer: {
    id: "answer.spec.map.layer",
    defaultMessage: "Слой",
    description: "Название поля в карточке карты ответа ИИ: слой карты",
  },
  mapSelection: {
    id: "answer.spec.map.selection",
    defaultMessage: "Выборка",
    description: "Название поля в карточке карты ответа ИИ: какие объекты выбраны",
  },
  mapOpen: {
    id: "answer.spec.map.open",
    defaultMessage: "открыть карту",
    description: "Ссылка из ответа ИИ на экран карты; строчными",
  },
  mapFrames: {
    id: "answer.spec.map.frames",
    defaultMessage: "Кадры контуров ответа",
    description: "Заголовок раскрытия с кадрами контуров в ответе ИИ",
  },
  mapFramesSummary: {
    id: "answer.spec.map.framesSummary",
    defaultMessage: "контуров: {count}",
    description: "Подпись-сводка раскрытия кадров контуров; {count} — число контуров",
  },
  mapFramesMore: {
    id: "answer.spec.map.framesMore",
    defaultMessage: "показать кадры ({count})",
    description: "Кнопка раскрытия кадров контуров в ответе ИИ; {count} — число контуров",
  },
  mapNoFrame: {
    id: "answer.spec.map.noFrame",
    defaultMessage: "Кадр карты здесь не рисуется: выборка называет не контуры, а слой и область, — геометрию этой выборки хранит карта.",
    description: "Пояснение в карточке карты ответа ИИ: превью нет, потому что выбраны не отдельные контуры",
  },
  timelineTitle: {
    id: "answer.spec.timeline.title",
    defaultMessage: "Как это шло",
    description: "Заголовок временной шкалы в ответе ИИ",
  },
  timelineDenominator: {
    id: "answer.spec.timeline.denominator",
    defaultMessage: "точек ответа",
    description: "Подпись единицы счёта шкалы в ответе ИИ: что считаем",
  },
  timelineCaption: {
    id: "answer.spec.timeline.caption",
    defaultMessage: "Даты точек ответа и что в них сказано",
    description: "Подпись (для чтения с экрана) временной шкалы в ответе ИИ",
  },
  timelineNoDate: {
    id: "answer.spec.timeline.noDate",
    defaultMessage: "дата точки не разрешилась",
    description: "Пометка у точки шкалы в ответе ИИ: дату не удалось определить",
  },
  timelineNoAsOf: {
    id: "answer.spec.timeline.noAsOf",
    defaultMessage: "Даты среза в ответе нет",
    description: "Пометка у шкалы в ответе ИИ: в ответе нет даты, на которую посчитано",
  },
  timelineEmpty: {
    id: "answer.spec.timeline.empty",
    defaultMessage: "Ни одной датированной точки",
    description: "Пустое состояние временной шкалы в ответе ИИ",
  },
  trendTitle: {
    id: "answer.spec.trend.title",
    defaultMessage: "Ряд величин",
    description: "Заголовок карточки ряда величин по датам в ответе ИИ",
  },
  trendPoints: {
    id: "answer.spec.trend.points",
    defaultMessage: "Точки ряда",
    description: "Заголовок диаграммы с точками ряда в ответе ИИ",
  },
  trendDenominator: {
    id: "answer.spec.trend.denominator",
    defaultMessage: "дата точки",
    description: "Подпись единицы счёта диаграммы ряда: что откладывается по строкам",
  },
  trendValueHeader: {
    id: "answer.spec.trend.valueHeader",
    defaultMessage: "величина",
    description: "Заголовок колонки величин в таблице ряда",
  },
  trendCaption: {
    id: "answer.spec.trend.caption",
    defaultMessage: "Величины ответа по датам точек",
    description: "Подпись (для чтения с экрана) диаграммы ряда в ответе ИИ",
  },
  trendNoValue: {
    id: "answer.spec.trend.noValue",
    defaultMessage: "величины нет",
    description: "Пометка у точки ряда, для которой величины нет",
  },
  trendFlat: {
    id: "answer.spec.trend.flat",
    defaultMessage: "без изменения",
    description: "Направление ряда между двумя точками: значение не изменилось; подставляется в «Направление между крайними точками: …»",
  },
  trendUp: {
    id: "answer.spec.trend.up",
    defaultMessage: "в сторону роста",
    description: "Направление ряда между двумя точками: рост; подставляется в «Направление между крайними точками: …»",
  },
  trendDown: {
    id: "answer.spec.trend.down",
    defaultMessage: "в сторону снижения",
    description: "Направление ряда между двумя точками: снижение; подставляется в «Направление между крайними точками: …»",
  },
  trendDirection: {
    id: "answer.spec.trend.direction",
    defaultMessage: "Направление между крайними точками: {direction}.",
    description: "Примечание под рядом из двух точек в ответе ИИ; {direction} — слова «в сторону роста» и т. п.",
  },
  compareTitle: {
    id: "answer.spec.compare.title",
    defaultMessage: "Сравнение",
    description: "Заголовок карточки сравнения в ответе ИИ",
  },
  compareOf: {
    id: "answer.spec.compare.of",
    defaultMessage: "из <value>{text}</value>",
    description: "Знаменатель в строке сравнения в ответе ИИ: «12 из 40»; <value> оборачивает число, {text} — само число",
  },
  originNoMethod: {
    id: "answer.spec.origin.noMethod",
    defaultMessage: "метод не назван",
    description: "Пометка под блоком ответа ИИ: сервер не назвал метод расчёта",
  },
  coverage: {
    id: "answer.spec.coverage.summary",
    defaultMessage: "покрытие: {observed} из {eligible}",
    description: "Подпись-сводка раскрытия «Подробность ответа»: сколько объектов с данными из скольки; аргументы — числа",
  },
  coverageUnknown: {
    id: "answer.spec.coverage.summaryUnknown",
    defaultMessage: "покрытие: {observed} из {eligible} · без данных: {unknown}",
    description: "Подпись-сводка раскрытия «Подробность ответа» при наличии объектов без данных; аргументы — числа",
  },
  detailTitle: {
    id: "answer.spec.detail.title",
    defaultMessage: "Подробность ответа",
    description: "Заголовок раскрытия с подробными блоками ответа ИИ",
  },
  limitsAria: {
    id: "answer.spec.limits.aria",
    defaultMessage: "Ограничения вывода",
    description: "Подпись (для чтения с экрана) блока ограничений под ответом ИИ",
  },
  howButton: {
    id: "answer.spec.how.button",
    defaultMessage: "Как это собрано",
    description: "Кнопка под ответом ИИ, открывает панель с основанием ответа",
  },
  howTitle: {
    id: "answer.spec.how.title",
    defaultMessage: "Как это собрано",
    description: "Заголовок боковой панели с основанием ответа ИИ",
  },
  howKicker: {
    id: "answer.spec.how.kicker",
    defaultMessage: "Основание ответа: срез, правила, источники и сама спецификация",
    description: "Подзаголовок боковой панели «Как это собрано» (срез — снимок данных на дату)",
  },
  roleplanner: {
    id: "answer.spec.model.role.planner",
    defaultMessage: "разбор вопроса",
    description: "Роль языковой модели в ответе ИИ: разобрать вопрос на вызовы; подставляется в «Языковая модель применена — …»",
  },
  rolephrasing: {
    id: "answer.spec.model.role.phrasing",
    defaultMessage: "подбор слов",
    description: "Роль языковой модели в ответе ИИ: подобрать слова; подставляется в «Языковая модель применена — …»",
  },
  modelNone: {
    id: "answer.spec.model.none",
    defaultMessage: "Языковая модель не использовалась: ответ собран правилами реестра и словарём фраз.",
    description: "Примечание под ответом ИИ: модель не участвовала",
  },
  modelUsed: {
    id: "answer.spec.model.used",
    defaultMessage: "Языковая модель применена — {role} · политика вывода {policy}{masked, select, yes { · значения маскированы перед отправкой} other {}}.",
    description: "Примечание под ответом ИИ: модель участвовала; {role} — её роль, {policy} — версия политики вывода данных, {masked} — yes, если значения были замаскированы",
  },
  scopeDistrict: {
    id: "answer.spec.scope.district",
    defaultMessage: "район",
    description: "Область пользователя в панели «Как это собрано»: весь район",
  },
  scopeFarm: {
    id: "answer.spec.scope.farm",
    defaultMessage: "хозяйства аккаунта",
    description: "Область пользователя в панели «Как это собрано»: только хозяйства этого аккаунта",
  },
  scopeUnscoped: {
    id: "answer.spec.scope.unscoped",
    defaultMessage: "область не выдана",
    description: "Область пользователя в панели «Как это собрано»: у аккаунта нет области",
  },
  scopeAnonymous: {
    id: "answer.spec.scope.anonymous",
    defaultMessage: "без входа: открытое чтение района",
    description: "Область пользователя в панели «Как это собрано»: гость читает открытые данные района",
  },
  howTool: {
    id: "answer.spec.how.tool",
    defaultMessage: "Инструмент",
    description: "Название строки в панели «Как это собрано»: инструмент реестра, посчитавший ответ",
  },
  howScope: {
    id: "answer.spec.how.scope",
    defaultMessage: "Область",
    description: "Название строки в панели «Как это собрано»: область данных пользователя",
  },
  howLevel: {
    id: "answer.spec.how.level",
    defaultMessage: "Уровень ответа",
    description: "Название строки в панели «Как это собрано»: на каком уровне посчитан ответ",
  },
  howSnapshot: {
    id: "answer.spec.how.snapshot",
    defaultMessage: "Срез",
    description: "Название строки в панели «Как это собрано»: срез — снимок данных на дату",
  },
  snapshotNone: {
    id: "answer.spec.how.snapshotNone",
    defaultMessage: "принятого среза нет · состояние на {date}",
    description: "Значение строки «Срез» в панели «Как это собрано»; {date} — дата",
  },
  snapshotPub: {
    id: "answer.spec.how.snapshotPub",
    defaultMessage: "публикация #{id} · состояние на {date}",
    description: "Значение строки «Срез» в панели «Как это собрано»; {id} — номер публикации среза, {date} — дата",
  },
  howDemo: {
    id: "answer.spec.how.demo",
    defaultMessage: "Демо-данные",
    description: "Название строки в панели «Как это собрано»: учтены ли демонстрационные данные",
  },
  demoIn: {
    id: "answer.spec.how.demoIn",
    defaultMessage: "включены в счёт",
    description: "Значение строки «Демо-данные»: демонстрационные данные учтены",
  },
  demoOut: {
    id: "answer.spec.how.demoOut",
    defaultMessage: "исключены из счёта",
    description: "Значение строки «Демо-данные»: демонстрационные данные не учтены",
  },
  howClasses: {
    id: "answer.spec.how.classes",
    defaultMessage: "Классы величин",
    description: "Название строки в панели «Как это собрано»: классы достоверности величин (факт, расчёт…)",
  },
  howCoverage: {
    id: "answer.spec.how.coverage",
    defaultMessage: "Покрытие",
    description: "Название строки в панели «Как это собрано»: у скольких объектов есть данные",
  },
  coverageValue: {
    id: "answer.spec.how.coverageValue",
    defaultMessage: "популяция {eligible} · с данными {observed} · без данных {unknown}",
    description: "Значение строки «Покрытие»; популяция — все объекты, о которых спрашивали; аргументы — числа",
  },
  howVersions: {
    id: "answer.spec.how.versions",
    defaultMessage: "Версии",
    description: "Название строки в панели «Как это собрано»: версии правил и реестра",
  },
  versionsValue: {
    id: "answer.spec.how.versionsValue",
    defaultMessage: "правила {rules} · реестр {registry}",
    description: "Значение строки «Версии»; аргументы — номера версий",
  },
  howHash: {
    id: "answer.spec.how.hash",
    defaultMessage: "Отпечаток значений",
    description: "Название строки в панели «Как это собрано»: контрольный отпечаток чисел ответа",
  },
  howRecord: {
    id: "answer.spec.how.record",
    defaultMessage: "Запись журнала",
    description: "Название строки в панели «Как это собрано»: номер записи в журнале вызовов",
  },
  sourcesTitle: {
    id: "answer.spec.sources.title",
    defaultMessage: "Источники",
    description: "Заголовок раскрытия со списком источников данных ответа",
  },
  sourcesSummary: {
    id: "answer.spec.sources.summary",
    defaultMessage: "записей: {count}",
    description: "Подпись-сводка раскрытия «Источники»; {count} — число записей",
  },
  sourceDefault: {
    id: "answer.spec.sources.default",
    defaultMessage: "источник",
    description: "Название источника, когда у него нет подписи; строчными",
  },
  sourceRow: {
    id: "answer.spec.sources.row",
    defaultMessage: "строка #{id}",
    description: "Часть строки источника: номер строки исходных данных; {id} — номер",
  },
  sourceDerivation: {
    id: "answer.spec.sources.derivation",
    defaultMessage: "вывод: {method}",
    description: "Часть строки источника: как получено значение; {method} — название метода",
  },
  traceTitle: {
    id: "answer.spec.trace.title",
    defaultMessage: "Трасса проверки",
    description: "Заголовок раскрытия со списком проверок ответа ИИ",
  },
  traceSummaryOk: {
    id: "answer.spec.trace.summaryOk",
    defaultMessage: "правил пересчитано: {recounted}, расхождений нет; на сервере: {server}",
    description: "Подпись-сводка раскрытия «Трасса проверки», когда расхождений нет; аргументы — числа",
  },
  traceSummaryDrift: {
    id: "answer.spec.trace.summaryDrift",
    defaultMessage: "расхождений: {drift} из {recounted} пересчитанных",
    description: "Подпись-сводка раскрытия «Трасса проверки» при расхождениях; аргументы — числа",
  },
  traceNote: {
    id: "answer.spec.trace.note",
    defaultMessage: "Правила пересчитаны КЛИЕНТОМ по этому конверту. Сам конверт списка проверок не несёт: принятый ответ молчит о проверке, а имя правила называет только отклонённый. Расхождение здесь означает не спорные данные, а то, что ответ и его проверка разошлись по дороге.",
    description: "Пояснение над списком проверок ответа ИИ; «клиент» — само приложение, «конверт» — ответ сервера целиком",
  },
  specTitle: {
    id: "answer.spec.json.title",
    defaultMessage: "Спецификация ответа",
    description: "Заголовок раскрытия с полным текстом ответа в служебном виде",
  },
  specSummary: {
    id: "answer.spec.json.summary",
    defaultMessage: "как есть",
    description: "Подпись-сводка раскрытия «Спецификация ответа»: показано без обработки",
  },
  traceHeld: {
    id: "answer.spec.trace.held",
    defaultMessage: "сошлось",
    description: "Бейдж у строки проверки: правило пересчитано и выполнено; коротко",
  },
  traceDriftBadge: {
    id: "answer.spec.trace.driftBadge",
    defaultMessage: "расхождение",
    description: "Бейдж у строки проверки: правило пересчитано и не выполнено; коротко",
  },
  traceServer: {
    id: "answer.spec.trace.server",
    defaultMessage: "проверено на сервере",
    description: "Бейдж у строки проверки: правило проверил сервер; коротко",
  },
  traceServerHint: {
    id: "answer.spec.trace.serverHint",
    defaultMessage: "Правило смотрит в реестр или словарь: клиенту его не пересчитать",
    description: "Подсказка у бейджа «проверено на сервере»",
  },
  traceClientHint: {
    id: "answer.spec.trace.clientHint",
    defaultMessage: "Правило пересчитано клиентом по этому конверту",
    description: "Подсказка у бейджей «сошлось» и «расхождение»",
  },
  traceRule: {
    id: "answer.spec.trace.rule",
    defaultMessage: "правило {rule}",
    description: "Название правила проверки в строке трассы; {rule} — обозначение правила (1, 4б…), не переводится",
  },
  abstTitle: {
    id: "answer.spec.abstention.title",
    defaultMessage: "Ответа не будет, и вот почему",
    description: "Заголовок панели отказа ИИ: ответа не будет; под ним причина",
  },
  abstNearest: {
    id: "answer.spec.abstention.nearest",
    defaultMessage: "Ближе всего к вопросу:",
    description: "Подпись над списком ближайших инструментов в панели отказа ИИ",
  },
  rejectedTitle: {
    id: "answer.spec.rejected.title",
    defaultMessage: "Ответ собран неверно и не показан",
    description: "Заголовок панели: проверка отклонила ответ ИИ, показать нечего",
  },
  rejectedText: {
    id: "answer.spec.rejected.text",
    defaultMessage: "Проверка ответа отклонила его правилом {rule}. Это ошибка нашей стороны, а не отказ по вашему вопросу: запись сохранена в журнале.",
    description: "Текст панели отклонённого ответа ИИ; {rule} — обозначение правила (1, 4б…)",
  },
  rejectedTextRecord: {
    id: "answer.spec.rejected.textRecord",
    defaultMessage: "Проверка ответа отклонила его правилом {rule}. Это ошибка нашей стороны, а не отказ по вашему вопросу: запись сохранена в журнале ({record}).",
    description: "Текст панели отклонённого ответа ИИ; {rule} — обозначение правила, {record} — номер записи журнала",
  },
  readinessFactual: {
    id: "answer.spec.readiness.factual",
    defaultMessage: "фактические данные",
    description: "Готовность данных инструмента ИИ: есть фактические данные (бейдж, строчными)",
  },
  readinessDerived: {
    id: "answer.spec.readiness.derived",
    defaultMessage: "производные величины",
    description: "Готовность данных инструмента ИИ: только рассчитанные величины (бейдж, строчными)",
  },
  readinessDemo: {
    id: "answer.spec.readiness.demo",
    defaultMessage: "только демонстрационные данные",
    description: "Готовность данных инструмента ИИ: только демонстрационные данные (бейдж, строчными)",
  },
  readinessMissing: {
    id: "answer.spec.readiness.missing",
    defaultMessage: "данных не поступало",
    description: "Готовность данных инструмента ИИ: данных нет (бейдж, строчными)",
  },
});

/** Название уровня (единицы строки) для середины предложения. */
export function grainLabel(level: LevelId): string {
  return levelWord(level);
}

/** Готовность данных словами. Набор закрыт контрактом; неизвестный код печатается как есть. */
export function readinessLabel(code: string): string {
  switch (code) {
    case "factual":
      return tr(m.readinessFactual);
    case "derived":
      return tr(m.readinessDerived);
    case "demo":
      return tr(m.readinessDemo);
    case "missing":
      return tr(m.readinessMissing);
    default:
      return code;
  }
}

/** Что нужно рендереру помимо самого конверта. */
export interface AnswerViewContext {
  /** Контекст анализа в адресе перехода: срез едет вместе со ссылкой. */
  linkSearch: (extra?: Record<string, string | number>) => string;
  /**
   * Область УЧАСТНИКА — из сессии, как и велит канон.
   *
   * Вывести её из ответа нельзя, и попытка стоила неправды на экране:
   * `spec.scope.kind` — область ЗАПИСИ РЕЕСТРА на выбранном уровне, а не
   * область аккаунта. У районного участника вызов инструмента на уровне
   * хозяйства приходит с `kind = actor`, и подпись печатала «хозяйства
   * аккаунта» там, где считался район. Числа при этом были верные — врала
   * ровно подпись, то есть самое проверяемое место ответа.
   *
   * `undefined` — «область не сообщена»: тогда строка про неё не печатается
   * вовсе. Придумать её здесь было бы тем же дефектом, только тише.
   */
  accountScope?: RoleKind;
}

/* ------------------------------------------------------------------ */
/* Фраза                                                               */
/* ------------------------------------------------------------------ */

/**
 * Текст фразы. `null` — ошибка сборки ответа на сервере: слот не разрешился.
 *
 * Такой случай назван вслух, а не спрятан: молчание выглядело бы как «здесь
 * ничего и не было», и поломка дошла бы до продакшена незамеченной.
 */
export function PhraseText({ phrase }: { phrase: AiPhrase | null | undefined }) {
  const t = useT();
  const text = phraseText(phrase);
  if (text) return <>{text}</>;
  return <span className={styles.missing}>{t(m.phraseMissing)}</span>;
}

function Value({
  resolved,
  refer,
}: {
  resolved: AiResolvedAnswer | undefined;
  refer: AiValueRef | undefined;
}) {
  const shown = showValue(resolved, refer);
  return (
    <span className={shown.missing ? styles.missing : styles.value}>
      {shown.text}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Переходы                                                            */
/* ------------------------------------------------------------------ */

/**
 * Переход по адресу ответа.
 *
 * Адрес, которого клиент не знает, ссылкой НЕ становится: он называется
 * словами. Неработающая ссылка обещает экран, которого нет, и человек
 * возвращается «назад» вместо ответа.
 */
function AnswerLink({
  to,
  label,
  view,
}: {
  to: string;
  label: string;
  view: AnswerViewContext;
}) {
  const t = useT();
  if (!knownRoute(to)) {
    return (
      <span className={styles.deadEnd} title={t(m.deadEndHint, { to })}>
        {t(m.deadEnd, { label })}
      </span>
    );
  }
  const [path] = to.split("?");
  return (
    <Link to={{ pathname: path, search: view.linkSearch() }}>{label}</Link>
  );
}

/* ------------------------------------------------------------------ */
/* Блоки                                                               */
/* ------------------------------------------------------------------ */

interface BlockProps {
  block: AiBlock;
  resolved: AiResolvedAnswer | undefined;
  view: AnswerViewContext;
  /**
   * Дата среза ответа. Нужна ровно одному блоку — истории: линия «на какой
   * день посчитано» принадлежит ОТВЕТУ, а не точке, и выдумывать её из
   * сегодняшнего дня нельзя. Отказ и отклонение её не несут, поэтому тип
   * допускает `null`.
   */
  asOf: string | null;
}

/**
 * Один блок спецификации.
 *
 * `switch` по `type` намеренно исчерпывающий: блок контракта без ветки — это
 * ошибка типизации, а не пустое место на экране. Образцы блоков порождает
 * backend-тест, поэтому «ветки нет» ловится ещё и тестом рендерера.
 */
export function BlockView({ block, resolved, view, asOf }: BlockProps) {
  const t = useT();
  switch (block.type) {
    case "demo_notice":
      // Тем же баннером, что и переключатель демо в шапке: человек уже видел
      // эту плашку и узнаёт её, не читая заново.
      return (
        <InlineAlert title={t(m.demoTitle)}>
          <PhraseText phrase={block.text} />
        </InlineAlert>
      );

    case "verdict":
      return (
        <Card hero>
          <p className={styles.verdict}>
            <PhraseText phrase={block.text} />
          </p>
          {block.values && block.values.length > 0 ? (
            <div className={styles.verdictValues}>
              {block.values.map((item) => (
                <Value key={item.id} resolved={resolved} refer={item} />
              ))}
            </div>
          ) : null}
          <BlockOrigin block={block} tone="hero" />
        </Card>
      );

    case "kpi_row": {
      const items = block.items ?? [];
      const { shown, hidden } = splitByBudget(items, DENSITY.headline);
      return (
        <>
          <div className={styles.kpiRow}>
            {shown.map((item, index) => (
              <MetricCard
                key={`${item.label}-${index}`}
                label={srv(item.label)}
                value={<Value resolved={resolved} refer={item.value} />}
                bottom={
                  <span className={styles.denominator}>
                    {item.drill_down ? (
                      <AnswerLink
                        to={item.drill_down}
                        label={srv(item.denominator_words)}
                        view={view}
                      />
                    ) : (
                      srv(item.denominator_words)
                    )}
                  </span>
                }
              />
            ))}
          </div>
          {hidden.length > 0 ? (
            <Disclosure
              title={t(m.restNumbers)}
              summary={t(m.moreSummary, { count: hidden.length })}
              more={t(m.moreAction, { count: hidden.length })}
            >
              <div className={styles.kpiRow}>
                {hidden.map((item, index) => (
                  <MetricCard
                    key={`${item.label}-${index}`}
                    label={srv(item.label)}
                    value={<Value resolved={resolved} refer={item.value} />}
                    bottom={
                      <span className={styles.denominator}>
                        {srv(item.denominator_words)}
                      </span>
                    }
                  />
                ))}
              </div>
            </Disclosure>
          ) : null}
        </>
      );
    }

    case "done_split":
      return <DoneSplitBlockView block={block} resolved={resolved} view={view} />;

    case "table":
      return <TableBlockView block={block} resolved={resolved} view={view} />;

    case "map":
      return <MapBlockView block={block} view={view} />;

    case "gap": {
      // Пробел — это БЛОК, а не пустота: «нет данных» названо числом и
      // причиной. Панель состояния здесь та же, что на экранах выдачи.
      const items = block.items ?? [];
      const { shown, hidden } = splitByBudget(items, DENSITY.preview);
      return (
        <DataStatePanel state="missing" title={t(m.gapTitle)}>
          <ul className={styles.list}>
            {shown.map((item, index) => (
              <li key={`${item.label}-${index}`}>
                <span className={styles.listLabel}>{srv(item.label)}</span>{" "}
                <Value resolved={resolved} refer={item.count} />
                <div className={styles.listReason}>
                  <PhraseText phrase={item.reason} />
                </div>
              </li>
            ))}
          </ul>
          {hidden.length > 0 ? (
            <Disclosure
              tone="inline"
              title={t(m.gapMore)}
              summary={t(m.moreSummary, { count: hidden.length })}
            >
              <ul className={styles.list}>
                {hidden.map((item, index) => (
                  <li key={`${item.label}-${index}`}>
                    <span className={styles.listLabel}>{srv(item.label)}</span>{" "}
                    <Value resolved={resolved} refer={item.count} />
                  </li>
                ))}
              </ul>
            </Disclosure>
          ) : null}
        </DataStatePanel>
      );
    }

    case "evidence_chain":
      return (
        <Card title={t(m.chainTitle)}>
          <ol className={styles.chain}>
            {(block.steps ?? []).map((step, index) => (
              <li key={`${step.method}-${index}`}>
                <EvidenceBadge status={step.epistemic} /> {srv(step.method)}
                {step.sources && step.sources.length > 0 ? (
                  <span className={styles.sources}>
                    {" · "}
                    {t(m.chainSources, { count: step.sources.length })}
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </Card>
      );

    case "actions":
      return (
        <div className={styles.actions}>
          {(block.items ?? []).map((item, index) => (
            <span key={`${item.to}-${index}`} className={styles.action}>
              <AnswerLink to={item.to} label={item.label} view={view} />
              {item.kind === "draft" ? (
                <Badge title={t(m.draftHint)}>{t(m.draftBadge)}</Badge>
              ) : null}
            </span>
          ))}
        </div>
      );

    case "deviations": {
      const items = block.items ?? [];
      const { shown, hidden } = splitByBudget(items, DENSITY.preview);
      const line = (item: (typeof items)[number], index: number) => (
        <li key={index}>
          <PhraseText phrase={item.text} />
          {item.value ? (
            <>
              {" "}
              <Value resolved={resolved} refer={item.value} />
            </>
          ) : null}
          {item.drill_down ? (
            <div className={styles.listReason}>
              <AnswerLink to={item.drill_down} label={t(m.open)} view={view} />
            </div>
          ) : null}
        </li>
      );
      return (
        <Card title={t(m.deviationsTitle)}>
          <ul className={styles.list}>{shown.map(line)}</ul>
          {hidden.length > 0 ? (
            <Disclosure
              tone="inline"
              title={t(m.deviationsMore)}
              summary={t(m.moreSummary, { count: hidden.length })}
            >
              <ul className={styles.list}>{hidden.map(line)}</ul>
            </Disclosure>
          ) : null}
          <BlockOrigin block={block} />
        </Card>
      );
    }

    case "causes":
      // Причина без класса уверенности — это утверждение, выданное за факт.
      // Поэтому бейдж стоит У КАЖДОГО пункта, а основание печатается рядом с
      // ним, а не в общем подвале: гипотеза и наблюдение различаются здесь.
      return (
        <Card title={t(m.causesTitle)}>
          <ul className={styles.list}>
            {(block.items ?? []).map((item, index) => (
              <li key={index}>
                <Badge
                  tone={item.confidence === "observed" ? "source" : "unknown"}
                  title={
                    item.confidence === "observed"
                      ? t(m.observedHint)
                      : t(m.hypothesisHint)
                  }
                >
                  {item.confidence === "observed" ? t(m.observed) : t(m.hypothesis)}
                </Badge>{" "}
                <PhraseText phrase={item.text} />
                <div className={styles.listReason}>
                  {item.basis ? (
                    <>
                      {item.basis.sources && item.basis.sources.length > 0
                        ? t(m.basisSources, {
                            method: item.basis.method,
                            count: item.basis.sources.length,
                          })
                        : t(m.basis, { method: item.basis.method })}
                    </>
                  ) : (
                    <span className={styles.missing}>{t(m.noBasis)}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <BlockOrigin block={block} />
        </Card>
      );

    case "timeline":
      return (
        <TimelineBlockView block={block} resolved={resolved} asOf={asOf} />
      );

    case "trend":
      return <TrendBlockView block={block} resolved={resolved} />;

    case "compare":
      return <CompareBlockView block={block} resolved={resolved} />;
  }
}

/* -- две оси -------------------------------------------------------- */

function DoneSplitBlockView({
  block,
  resolved,
}: {
  block: Extract<AiBlock, { type: "done_split" }>;
  resolved: AiResolvedAnswer | undefined;
  view: AnswerViewContext;
}) {
  const t = useT();
  const split = block.split;
  const [first, second] = block.axes.map((axis) => srv(axis));

  if (split.outcome === "not_assigned") {
    return (
      <Card title={`${first} / ${second}`}>
        <DoneSplit
          counts={{ total: 0, reported: 0, verified: 0 }}
          noun={m.nounField}
          absent={phraseText(split.note) ?? undefined}
          size="hero"
        />
      </Card>
    );
  }
  if (split.outcome === "uncountable") {
    return (
      <Card title={`${first} / ${second}`}>
        <DoneSplit
          counts={null}
          noun={m.nounField}
          absent={phraseText(split.reason) ?? undefined}
          size="hero"
        />
      </Card>
    );
  }

  const total = numberOf(resolved, split.total);
  const reported = numberOf(resolved, split.reported);
  const verified = numberOf(resolved, split.verified);
  const noReport = numberOf(resolved, split.no_report);
  if (total === null || reported === null || verified === null) {
    // Счёт объявлен, а величин нет — это поломка сборки ответа, и она
    // называется словами, а не рисуется нулями.
    return (
      <Card title={`${first} / ${second}`}>
        <DoneSplit
          counts={null}
          noun={m.nounField}
          absent={t(m.splitNoValues)}
          size="hero"
        />
      </Card>
    );
  }
  // Остаток «без отчёта» приходит СВОЕЙ величиной, а `DoneSplit` считает его
  // сам. Совпадать они обязаны; расхождение — это разные знаменатели у одной
  // развилки, и молчать о нём нельзя.
  const drift = noReport !== null && noReport !== total - reported;
  return (
    <Card title={`${first} / ${second}`}>
      <DoneSplit
        counts={{ total, reported, verified }}
        noun={m.nounField}
        size="hero"
      />
      {drift ? (
        <p className={styles.missing}>
          {t(m.splitDrift, { value: noReport })}
        </p>
      ) : null}
      <BlockOrigin block={block} />
    </Card>
  );
}

/* -- таблица -------------------------------------------------------- */

/**
 * Окно прокрутки ничего не догружает: ответ приехал целиком.
 *
 * Константа, а не стрелка на месте: новая функция каждый рендер попадала бы в
 * зависимости эффекта `VirtualTable` и гоняла бы его на каждый кадр.
 */
const NO_PAGING = () => undefined;

/**
 * Таблица ответа: первые строки на виду, ХВОСТ — виртуализированной таблицей.
 *
 * Разделение труда здесь такое. Первый взгляд — восемь строк бюджета, и они
 * нарисованы как есть: восемь узлов виртуализировать нечего, а окно поверх
 * восьми строк только добавило бы прокрутку внутри прокрутки.
 *
 * Хвост — `VirtualTable`, тот самый примитив, а не его таблица стилей. Ответ
 * над зерном контура приезжает сотнями строк, и раскрытие, кладущее их все в
 * DOM, превращает жест «показать ещё» в подвисание на полсекунды. Окно решает
 * ровно это.
 *
 * Скелета незагруженной строки здесь не бывает, и это не случайность:
 * `rows` отдаётся ПОЛНОСТЬЮ (ответ приехал целиком), а `onRangeChange` —
 * пустышка, потому что догружать неоткуда. Скелет обещал бы подгрузку,
 * которой нет; отсутствие скелета — следствие полноты карты строк, а не
 * умолчание.
 */
function TableBlockView({
  block,
  resolved,
  view,
}: {
  block: Extract<AiBlock, { type: "table" }>;
  resolved: AiResolvedAnswer | undefined;
  view: AnswerViewContext;
}) {
  const t = useT();
  const columns = block.columns ?? [];
  const rows = block.rows ?? [];
  const { shown, hidden } = splitByBudget(rows, DENSITY.tablePreview);
  const template = columns.map(() => "1fr").join(" ");

  // Первый взгляд рисуется ПОТОКОМ: `tableStyles.row` — строка окна
  // виртуализации, ей `top`/`height` задаёт VirtualTable, и вне окна она
  // схлопывает контейнер и кладёт все строки друг на друга. Поток — свой
  // класс `rowStatic`.
  const body = (list: typeof rows) => (
    <div className={tableStyles.flow}>
      {list.map((row) => (
        <div
          key={row.id}
          className={tableStyles.rowStatic}
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column, index) => {
            const cell = row.cells?.[index];
            const first = index === 0;
            return (
              <div key={column.key} className={tableStyles.cell}>
                {first && row.drill_down ? (
                  <AnswerLink
                    to={row.drill_down}
                    label={showValue(resolved, cell).text}
                    view={view}
                  />
                ) : (
                  <Value resolved={resolved} refer={cell} />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  // Колонки и строки хвоста — в той форме, которой их ждёт `VirtualTable`:
  // абсолютный индекс → строка. Карта полная: догружать неоткуда, и скелет
  // незагруженной строки в ответе появиться не может.
  const virtualColumns: Column<(typeof rows)[number]>[] = columns.map(
    (column, index) => ({
      key: column.key,
      header: srv(column.label),
      width: "1fr",
      render: (row) => {
        const cell = row.cells?.[index];
        if (index === 0 && row.drill_down) {
          return (
            <AnswerLink
              to={row.drill_down}
              label={showValue(resolved, cell).text}
              view={view}
            />
          );
        }
        return <Value resolved={resolved} refer={cell} />;
      },
    }),
  );
  const virtualRows = new Map(hidden.map((row, index) => [index, row]));

  if (rows.length === 0) {
    return (
      <DataStatePanel state="empty" title={t(m.tableEmpty)}>
        {block.empty ? (
          <PhraseText phrase={block.empty} />
        ) : (
          <span className={styles.missing}>{t(m.tableEmptyNoReason)}</span>
        )}
      </DataStatePanel>
    );
  }

  return (
    <div className={tableStyles.frame}>
      <div className={tableStyles.meta}>
        <span>
          {block.denominator
            ? t(m.tableGrainDenominator, {
                grain: grainLabel(block.grain),
                denominator: srv(block.denominator),
              })
            : t(m.tableGrain, { grain: grainLabel(block.grain) })}
        </span>
      </div>
      <div className={tableStyles.head} style={{ gridTemplateColumns: template }}>
        {columns.map((column) => (
          <div key={column.key} className={tableStyles.cell}>
            {srv(column.label)}
          </div>
        ))}
      </div>
      {body(shown)}
      {hidden.length > 0 ? (
        <Disclosure
          tone="inline"
          title={t(m.tableMore)}
          summary={t(m.moreSummary, { count: hidden.length })}
          more={t(m.moreAction, { count: hidden.length })}
        >
          <VirtualTable
            columns={virtualColumns}
            total={hidden.length}
            rows={virtualRows}
            onRangeChange={NO_PAGING}
            rowKey={(row) => row.id}
            caption={t(m.tableCaption, { count: hidden.length })}
            meta={
              <span>
                {t(m.tableMeta, {
                  total: rows.length,
                  preview: DENSITY.tablePreview,
                  hidden: hidden.length,
                })}
              </span>
            }
          />
        </Disclosure>
      ) : null}
    </div>
  );
}

/* -- карта ---------------------------------------------------------- */

/**
 * Карта ответа: кадр там, где выборка назвала контуры; слова — везде ещё.
 *
 * Геометрии в контракте нет намеренно (см. `MapBlock`), и долгое время из
 * этого следовал отказ рисовать что-либо. Следствие оказалось сильнее
 * причины: выборка вида `contour:1204` НАЗЫВАЕТ единицы поимённо, и показать
 * их превью — тот же жест, что на карте и в обзоре района, только начатый с
 * другой стороны. Свойства фичи берёт из карты `AnswerContourPreview`, и
 * берёт лениво: пока раскрытие свёрнуто, ответ не стоит экрану ни одного
 * запроса карты.
 *
 * Выборка, которая контуров не называет (массив, слой, область), по-прежнему
 * печатается словами и ведёт на карту: врать полигоном «примерно отсюда»
 * нельзя, а достраивать список контуров по массиву вторым запросом значило бы
 * гадать, что сервер имел в виду.
 */
function MapBlockView({
  block,
  view,
}: {
  block: Extract<AiBlock, { type: "map" }>;
  view: AnswerViewContext;
}) {
  const t = useT();
  const contours = selectionContours(block.selection);
  // Свёрнутый `<details>` держит детей в DOM, поэтому «свернули» само по себе
  // не означает «не запрашивали»: кадры монтируются только после жеста.
  const [framesOpen, setFramesOpen] = useState(false);
  return (
    <Card title={t(m.mapTitle)}>
      <dl className={styles.pairs}>
        <dt>{t(m.mapLayer)}</dt>
        <dd>{block.layer}</dd>
        <dt>{t(m.mapSelection)}</dt>
        <dd>{block.selection}</dd>
      </dl>
      <AnswerLink to={ROUTES.map} label={t(m.mapOpen)} view={view} />
      {contours.length > 0 ? (
        <Disclosure
          tone="inline"
          title={t(m.mapFrames)}
          summary={t(m.mapFramesSummary, { count: contours.length })}
          more={t(m.mapFramesMore, { count: contours.length })}
          onToggle={setFramesOpen}
        >
          {framesOpen ? <AnswerContourPreview ids={contours} /> : null}
        </Disclosure>
      ) : (
        <p className={styles.note}>{t(m.mapNoFrame)}</p>
      )}
      <BlockOrigin block={block} />
    </Card>
  );
}

/* -- история и ряд --------------------------------------------------- */

function TimelineBlockView({
  block,
  resolved,
  asOf,
}: {
  block: Extract<AiBlock, { type: "timeline" }>;
  resolved: AiResolvedAnswer | undefined;
  asOf: string | null;
}) {
  const t = useT();
  const points = block.points ?? [];
  // Точка истории — это МОМЕНТ, и полоса у неё нулевой длины: рисовать её
  // отрезком значило бы показать длительность, которой в данных нет.
  const rows = points.map((point, index) => {
    const value = resolved?.values?.[point.at.id];
    const iso = value && value.kind === "date" ? value.value : null;
    const text = phraseText(point.text) ?? "";
    return {
      key: `point-${index}`,
      label: <PhraseText phrase={point.text} />,
      labelText: text,
      bands: iso
        ? [{ key: `band-${index}`, from: iso, to: iso, tone: "window" as const }]
        : [],
      ...(iso ? {} : { absent: t(m.timelineNoDate) }),
    };
  });
  return (
    <Timeline
      title={t(m.timelineTitle)}
      denominator={t(m.timelineDenominator)}
      caption={t(m.timelineCaption)}
      rows={rows}
      asOf={asOf}
      asOfAbsent={t(m.timelineNoAsOf)}
      empty={t(m.timelineEmpty)}
    />
  );
}

/**
 * Ряд величин. Две точки — НЕ тренд, и это сказано словом, а не нарисовано
 * линией: линия между двумя точками читается как направление, которого в
 * данных нет.
 */
function TrendBlockView({
  block,
  resolved,
}: {
  block: Extract<AiBlock, { type: "trend" }>;
  resolved: AiResolvedAnswer | undefined;
}) {
  const t = useT();
  const points = block.points ?? [];
  const rows = points.map((point) => ({
    at: showById(resolved, point.at.id).text,
    value: showValue(resolved, point.value),
    raw: numberOf(resolved, point.value),
  }));
  const first = rows[0];
  const last = rows[rows.length - 1];
  const direction =
    rows.length === 2 && first?.raw !== null && last?.raw !== null
      ? first!.raw! === last!.raw!
        ? t(m.trendFlat)
        : first!.raw! < last!.raw!
          ? t(m.trendUp)
          : t(m.trendDown)
      : null;

  return (
    <Card title={t(m.trendTitle)}>
      <Bars
        title={t(m.trendPoints)}
        unit=""
        denominator={t(m.trendDenominator)}
        valueHeader={t(m.trendValueHeader)}
        caption={t(m.trendCaption)}
        data={rows.map((row, index) => ({
          key: `point-${index}`,
          label: row.at,
          labelText: row.at,
          value: row.raw,
          display: row.value.text,
          ...(row.raw === null ? { absent: t(m.trendNoValue) } : {}),
        }))}
      />
      {direction ? (
        // Только направление между крайними точками — и ни слова о том, тренд
        // это или нет: про тренд говорит САМ ОТВЕТ фразой ограничений
        // (`trend.two_points`), и повторять её здесь значило бы завести второе
        // место, где написан тот же текст.
        <p className={styles.note}>{t(m.trendDirection, { direction })}</p>
      ) : null}
      <BlockOrigin block={block} />
    </Card>
  );
}

/* -- сравнение ------------------------------------------------------- */

/**
 * Сравнение. Строка о различии знаменателей печатается ВСЕГДА, а «знаменатель
 * мал» — у каждой строки, не дотянувшей до порога: без этого сравнение читается
 * как оценка, а оценок хозяйств продукт не строит.
 */
function CompareBlockView({
  block,
  resolved,
}: {
  block: Extract<AiBlock, { type: "compare" }>;
  resolved: AiResolvedAnswer | undefined;
}) {
  const t = useT();
  const floor = block.floor;
  const items = block.items ?? [];
  return (
    <Card title={t(m.compareTitle)}>
      <p className={styles.note}>
        <PhraseText phrase={block.denominator_note} />
      </p>
      <div className={styles.compare}>
        {items.map((item, index) => {
          const denominator = numberOf(resolved, item.denominator);
          const below =
            floor !== null &&
            floor !== undefined &&
            denominator !== null &&
            denominator < floor.minimum;
          return (
            <div key={index} className={styles.compareRow}>
              <span className={styles.listLabel}>
                {showById(resolved, item.entity.id).text}
              </span>
              <Value resolved={resolved} refer={item.value} />
              <span className={styles.denominator}>
                {t(m.compareOf, {
                  text: showValue(resolved, item.denominator).text,
                  value: () => <Value resolved={resolved} refer={item.denominator} />,
                })}
                {floor ? ` ${srv(floor.denominator_words)}` : ""}
              </span>
              {below ? (
                <Badge tone="unknown">
                  <PhraseText phrase={floor!.below_floor_text} />
                </Badge>
              ) : null}
            </div>
          );
        })}
      </div>
      <BlockOrigin block={block} />
    </Card>
  );
}

/* -- происхождение блока --------------------------------------------- */

function BlockOrigin({
  block,
  tone = "card",
}: {
  block: AiBlock;
  /**
   * На чём нарисовано происхождение. `hero` — доминантный зелёный градиент, и
   * на нём `--muted` (тёмно-серый, рассчитанный на светлый фон) почти
   * неразличим: пояснение метода тонуло в подложке. Токен «текст на зелёном»
   * для того и заведён. Переопределением `.hero .origin` это не чинится:
   * `.hero` живёт в модуле карточек, `.origin` — в модуле ответа, и классы
   * разных модулей не встречаются в одном селекторе.
   */
  tone?: "card" | "hero";
}) {
  const t = useT();
  const provenance = "provenance" in block ? block.provenance : null;
  if (!provenance) return null;
  return (
    <p className={tone === "hero" ? styles.originHero : styles.origin}>
      <EvidenceBadge status={provenance.epistemic} />{" "}
      {provenance.method ? (
        srv(provenance.method)
      ) : (
        <span className={styles.missing}>{t(m.originNoMethod)}</span>
      )}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Конверт целиком                                                     */
/* ------------------------------------------------------------------ */

export function AnswerSpecView({
  envelope,
  view,
}: {
  envelope: AiEnvelope;
  view: AnswerViewContext;
}) {
  if (envelope.kind === "abstention") {
    return <AbstentionView envelope={envelope} />;
  }
  if (envelope.kind === "rejected") {
    return <RejectedView envelope={envelope} />;
  }
  return <AnswerView envelope={envelope} view={view} />;
}

function AnswerView({
  envelope,
  view,
}: {
  envelope: AiAnswerOk;
  view: AnswerViewContext;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const spec = envelope.spec;
  const blocks = spec.blocks ?? [];
  const disclosure = spec.disclosure ?? [];
  const limits = envelope.limits ?? [];

  const coverage = spec.coverage;
  const summary = useMemo(
    () =>
      coverage.unknown > 0
        ? t(m.coverageUnknown, {
            observed: coverage.observed,
            eligible: coverage.eligible,
            unknown: coverage.unknown,
          })
        : t(m.coverage, { observed: coverage.observed, eligible: coverage.eligible }),
    [coverage, t],
  );

  return (
    <div className={styles.answer}>
      {blocks.map((block) => (
        <BlockView
          key={block.id}
          block={block}
          resolved={envelope.resolved}
          view={view}
          asOf={envelope.basis.as_of}
        />
      ))}

      {disclosure.length > 0 ? (
        <Disclosure title={t(m.detailTitle)} summary={summary}>
          <div className={styles.answer}>
            {disclosure.map((block) => (
              <BlockView
                key={block.id}
                block={block}
                resolved={envelope.resolved}
                view={view}
                asOf={envelope.basis.as_of}
              />
            ))}
          </div>
        </Disclosure>
      ) : null}

      {limits.length > 0 ? (
        <footer className={styles.limits} aria-label={t(m.limitsAria)}>
          {limits.map((phrase, index) => (
            <p key={index}>
              <PhraseText phrase={phrase} />
            </p>
          ))}
        </footer>
      ) : null}

      <ModelUseNote model={envelope.model} />

      <div className={styles.howRow}>
        <Button onClick={() => setOpen(true)}>{t(m.howButton)}</Button>
      </div>

      <DetailDrawer
        open={open}
        title={t(m.howTitle)}
        kicker={t(m.howKicker)}
        onClose={() => setOpen(false)}
      >
        <HowItWasBuilt envelope={envelope} view={view} />
      </DetailDrawer>
    </div>
  );
}

/** Роль модели словами. Набор закрыт контрактом (``ModelRole``). */
const MODEL_ROLE: Record<string, PlainMessage> = {
  planner: m.roleplanner,
  phrasing: m.rolephrasing,
};

/**
 * Участие языковой модели — строкой ответа, а не умолчанием.
 *
 * Ответ шлюза собирается правилами реестра и словарём фраз, и модель в этом
 * пути участвует далеко не всегда. Утверждение «языковая модель не
 * использовалась» — половина того, ради чего слой устроен именно так, и
 * подразумевать его молча нельзя: человек, читающий число, обязан знать,
 * откуда взялись слова рядом с ним. Режим модели на станции говорит о ПЛАНЕ
 * (как разобран свободный вопрос) — это другое утверждение и другой путь:
 * прямой вызов инструмента из кармана уровня плана не имеет вовсе.
 *
 * Различаются варианты по наличию поля провайдера, а не по `used`: в схеме
 * клиента разделитель объединения приезжает строкой (`"False"`/`"True"`), а в
 * теле ответа — булевым, и сравнение с любым из двух было бы верным лишь
 * наполовину.
 */
function ModelUseNote({ model }: { model?: AiModelUse | null }) {
  const t = useT();
  if (!model) return null;
  if (!("provider" in model)) {
    return <p className={styles.note}>{t(m.modelNone)}</p>;
  }
  const role = MODEL_ROLE[model.role];
  return (
    <p className={styles.note}>
      {/* Провайдер и название модели не показываем (решение владельца 05.10). */}
      {t(m.modelUsed, {
        role: role ? t(role) : model.role,
        policy: model.policy_version,
        masked: model.masked ? "yes" : "no",
      })}
    </p>
  );
}

/** Область участника СЛОВАМИ. Источник один — сессия (`roleKind`). */
const SCOPE_WORDS: Record<RoleKind, PlainMessage> = {
  district: m.scopeDistrict,
  farm: m.scopeFarm,
  unscoped: m.scopeUnscoped,
  anonymous: m.scopeAnonymous,
};

/**
 * «Как это собрано» — основание ответа, а не его пересказ.
 *
 * Сюда сложено ровно то, чем ответ доказывается: срез и дата, версии правил и
 * реестра, арифметика покрытия, классы величин, происхождение, трасса проверки
 * и сама спецификация как есть.
 *
 * О трассе — отдельно, потому что её легко принять за чужую. Конверт `answer`
 * списка проверок НЕ несёт: принятый ответ по контракту молчит о том, что его
 * проверяли, а имя правила называет только отклонённый конверт. Трасса ниже
 * ПЕРЕСЧИТАНА клиентом над тем же конвертом, и сказано это прямо на экране:
 * выдать собственный счёт за серверный значило бы подделать доказательство.
 */
function HowItWasBuilt({
  envelope,
  view,
}: {
  envelope: AiAnswerOk;
  view: AnswerViewContext;
}) {
  const t = useT();
  const basis = envelope.basis;
  const provenance = envelope.provenance;
  const spec = envelope.spec;
  const trace = validatorTrace(envelope);
  const drift = traceDrift(trace);
  // Свой счёт и чужой — раздельно. Подпись раскрытия — это утверждение о том,
  // что ПРОВЕРЕНО, и складывать в него строки «проверено на сервере» значит
  // приписывать себе проверки, о которых абзацем ниже сказано, что клиент их
  // не делал. Число серверных строк названо рядом, иначе подпись выглядит
  // короче списка, который под ней раскрывается.
  const recounted = traceRecounted(trace);
  const onServer = trace.length - recounted;
  return (
    <div className={styles.how}>
      <dl className={styles.pairs}>
        <dt>{t(m.howTool)}</dt>
        <dd>{spec.tool_id}</dd>
        {view.accountScope ? (
          <>
            <dt>{t(m.howScope)}</dt>
            {/* Из сессии, а не из ответа: см. `accountScope`. */}
            <dd>{t(SCOPE_WORDS[view.accountScope])}</dd>
          </>
        ) : null}
        <dt>{t(m.howLevel)}</dt>
        <dd>
          {grainLabel(spec.scope.level)}
          {spec.scope.id !== null && spec.scope.id !== undefined
            ? ` #${spec.scope.id}`
            : ""}
        </dd>
        <dt>{t(m.howSnapshot)}</dt>
        <dd>
          {basis.snapshot.snapshot_id === null ||
          basis.snapshot.snapshot_id === undefined
            ? t(m.snapshotNone, { date: basis.as_of })
            : t(m.snapshotPub, {
                id: basis.snapshot.snapshot_id,
                date: basis.as_of,
              })}
        </dd>
        <dt>{t(m.howDemo)}</dt>
        <dd>{basis.include_demo ? t(m.demoIn) : t(m.demoOut)}</dd>
        <dt>{t(m.howClasses)}</dt>
        <dd className={styles.classes}>
          {(basis.classes_present ?? []).map((item) => (
            <EvidenceBadge key={item} status={item} />
          ))}
        </dd>
        <dt>{t(m.howCoverage)}</dt>
        <dd>
          {t(m.coverageValue, {
            eligible: spec.coverage.eligible,
            observed: spec.coverage.observed,
            unknown: spec.coverage.unknown,
          })}
        </dd>
        <dt>{t(m.howVersions)}</dt>
        <dd>
          {t(m.versionsValue, {
            rules: provenance.rules_version ?? "—",
            registry: provenance.registry_version ?? "—",
          })}
        </dd>
        <dt>{t(m.howHash)}</dt>
        <dd className={styles.hash}>{envelope.resolved.values_hash ?? "—"}</dd>
        <dt>{t(m.howRecord)}</dt>
        <dd className={styles.hash}>{envelope.record_id ?? "—"}</dd>
      </dl>

      <Disclosure
        tone="inline"
        title={t(m.sourcesTitle)}
        summary={t(m.sourcesSummary, { count: (provenance.sources ?? []).length })}
      >
        <ul className={styles.list}>
          {(provenance.sources ?? []).map((source, index) => (
            <li key={index}>
              {source.label ? srv(source.label) : t(m.sourceDefault)}
              {source.source_record_id
                ? ` · ${t(m.sourceRow, { id: source.source_record_id })}`
                : ""}
              {source.derivation
                ? ` · ${t(m.sourceDerivation, {
                    method: srv(
                      String((source.derivation as Record<string, unknown>).method ?? "—"),
                    ),
                  })}`
                : ""}
            </li>
          ))}
        </ul>
      </Disclosure>

      <Disclosure
        tone="inline"
        title={t(m.traceTitle)}
        summary={
          drift === 0
            ? t(m.traceSummaryOk, { recounted, server: onServer })
            : t(m.traceSummaryDrift, { drift, recounted })
        }
      >
        <p className={styles.note}>{t(m.traceNote)}</p>
        <ul className={styles.list}>
          {trace.map((row) => (
            <TraceLine key={row.rule} row={row} />
          ))}
        </ul>
      </Disclosure>

      <Disclosure tone="inline" title={t(m.specTitle)} summary={t(m.specSummary)}>
        <pre className={styles.json}>{JSON.stringify(spec, null, 2)}</pre>
      </Disclosure>
    </div>
  );
}

/** Одна строка трассы: правило, требование и что с ним стало. */
function TraceLine({ row }: { row: TraceRow }) {
  const t = useT();
  return (
    <li>
      <Badge
        tone={
          row.outcome === "held"
            ? "source"
            : row.outcome === "drift"
              ? "attention"
              : "unknown"
        }
        title={
          row.outcome === "server"
            ? t(m.traceServerHint)
            : t(m.traceClientHint)
        }
      >
        {row.outcome === "held"
          ? t(m.traceHeld)
          : row.outcome === "drift"
            ? t(m.traceDriftBadge)
            : t(m.traceServer)}
      </Badge>{" "}
      <span className={styles.listLabel}>{t(m.traceRule, { rule: row.rule })}</span> — {row.what}
      {row.drift ? (
        <div className={styles.listReason}>
          <span className={styles.missing}>{row.drift}</span>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Отказ — это ответ, а не сбой: у него есть причина, и она сказана словами
 * сервера. «Ближайшие инструменты» печатаются ТОЛЬКО когда они есть: пустой
 * список под словом «ближайший» был бы выдумкой.
 *
 * Плашка об участии модели стоит и здесь. Отказ — такое же утверждение о
 * корпусе, как число, и вопрос «кто это сказал» к нему относится ровно так
 * же: отказ, выданный моделью, и отказ, выданный правилом реестра, — разные
 * события с разной ценой ошибки. Конверт отказа несёт `model` тем же полем,
 * что и ответ, и молчать о нём значило бы сделать плашку выборочной, то есть
 * ненадёжной.
 */
export function AbstentionView({ envelope }: { envelope: AiAbstention }) {
  const t = useT();
  const nearest = envelope.nearest ?? [];
  return (
    <DataStatePanel state="info" title={t(m.abstTitle)}>
      <p>
        <PhraseText phrase={envelope.detail} />
      </p>
      {nearest.length > 0 ? (
        <div className={styles.nearest}>
          <span className={styles.listLabel}>{t(m.abstNearest)}</span>
          <ul className={styles.list}>
            {nearest.map((tool) => (
              <li key={tool.tool_id}>
                {tool.tool_id} · {grainLabel(tool.level)}
                {tool.readiness ? ` · ${readinessLabel(tool.readiness)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ModelUseNote model={envelope.model} />
    </DataStatePanel>
  );
}

/**
 * Отклонение валидатором — поломка реализации, и человеку говорится именно
 * это. Внутреннее объяснение (`explanation`) на экран НЕ выносится: оно
 * написано для разработчика и живёт в журнале.
 */
export function RejectedView({ envelope }: { envelope: AiRejected }) {
  const t = useT();
  return (
    <DataStatePanel state="error" title={t(m.rejectedTitle)}>
      <p>
        {envelope.record_id
          ? t(m.rejectedTextRecord, { rule: envelope.rule, record: envelope.record_id })
          : t(m.rejectedText, { rule: envelope.rule })}
      </p>
    </DataStatePanel>
  );
}


